import axios from 'axios'
import { resolveApiBaseURL, wsUrl } from '@/utils/appBase'
import { agentClientHeaders, getAgentClientId } from '@/utils/agentClientId'

// 创建 axios 实例
const api = axios.create({
  baseURL: resolveApiBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 可以在这里添加 token 等认证信息
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    const message = error.response?.data?.detail || error.message || '请求失败'
    return Promise.reject({ message, ...error })
  }
)

// 文章相关 API
export const articleApi = {
  // 获取文章列表
  getList: (params) => api.get('/api/articles', { params }),

  // 获取文章详情
  getById: (id) => api.get(`/api/articles/${id}`),

  // 创建文章
  create: (data) => api.post('/api/articles', data),

  // 更新文章
  update: (id, data) => api.put(`/api/articles/${id}`, data),

  // 删除文章
  delete: (id) => api.delete(`/api/articles/${id}`),

  // 批量删除
  batchDelete: (ids) => api.post('/api/articles/batch-delete', { ids }),

  // 导出文章
  export: (ids, format = 'markdown') => api.post('/api/articles/export', { ids, format }),

  // 发布到平台
  publish: (id, platform) => api.post(`/api/articles/${id}/publish`, { platform })
}

// 任务相关 API
export const taskApi = {
  // 获取任务列表
  getList: (params) => api.get('/api/tasks', { params }),

  // 获取任务详情
  getById: (id) => api.get(`/api/tasks/${id}`),

  // 创建生成任务
  create: (data) => api.post('/api/generate/batch', data),

  // 暂停任务
  pause: (id) => api.post(`/api/tasks/${id}/pause`),

  // 恢复任务
  resume: (id) => api.post(`/api/tasks/${id}/resume`),

  // 取消任务
  cancel: (id) => api.post(`/api/tasks/${id}/cancel`),

  // 批量删除
  batchDelete: (ids) => api.post('/api/tasks/batch-delete', { ids })
}

// 搜索相关 API
export const searchApi = {
  // 搜索素材
  search: (params) => api.get('/api/search', { params }),

  // 获取搜索缓存
  getCache: (topicTag) => api.get('/api/search/cache', { params: { topic_tag: topicTag } })
}

// 生成相关 API
export const generateApi = {
  // 单篇文章生成
  single: (data) => api.post('/api/generate/single', data),

  // 批量生成
  batch: (data) => api.post('/api/generate/batch', data)
}

/** 批量生成页 · 表单配置历史（后端 SQLite 持久化） */
export const formHistoryApi = {
  list: (params) => api.get('/api/generate/form-history', { params }),
  save: (data) => api.post('/api/generate/form-history', data),
  delete: (id) => api.delete(`/api/generate/form-history/${id}`)
}

/** Agent 助手 · 会话与对话（后端持久化） */
export const agentApi = {
  chat: (data) => {
    const client_id = getAgentClientId()
    return api.post('/api/agent/chat', { ...data, client_id }, { headers: agentClientHeaders() })
  },
  /** SSE 流式对话：请用 utils/agentStream.runAgentStream('/api/agent/chat/stream', ...) */
  chatStreamPath: '/api/agent/chat/stream',
  approve: (data) =>
    api.post('/api/agent/approve', data, { headers: agentClientHeaders() }),
  listSessions: () =>
    api.get('/api/agent/sessions', {
      params: { client_id: getAgentClientId() },
      headers: agentClientHeaders(),
    }),
  createSession: (data) => {
    const client_id = getAgentClientId()
    return api.post(
      '/api/agent/sessions',
      { ...(data || {}), client_id },
      { headers: agentClientHeaders() },
    )
  },
  getSession: (id) =>
    api.get(`/api/agent/sessions/${id}`, {
      params: { client_id: getAgentClientId() },
      headers: agentClientHeaders(),
    }),
  updateSession: (id, data) =>
    api.patch(`/api/agent/sessions/${id}`, data, {
      params: { client_id: getAgentClientId() },
      headers: agentClientHeaders(),
    }),
  deleteSession: (id) =>
    api.delete(`/api/agent/sessions/${id}`, {
      params: { client_id: getAgentClientId() },
      headers: agentClientHeaders(),
    }),
}

// 审查相关 API（敏感词检测 + 分类开关 + AI 改写）
export const reviewApi = {
  // 审查内容
  review: (data) => api.post('/api/review', data),

  // 敏感词分类开关
  getCategories: () => api.get('/api/review/categories'),
  updateCategories: (categories) => api.put('/api/review/categories', { categories }),

  // 对文本做敏感词检测（标红预览）
  check: (data) => api.post('/api/review/check', data),

  // AI 改写规避敏感词（改写后自动重审；apply=true 时落库）
  rewrite: (data) => api.post('/api/review/rewrite', data),
}

// 查重相关 API
export const dedupApi = {
  // 检查重复
  check: (data) => api.post('/api/dedup/check', data),

  // 批量查重
  batchCheck: (data) => api.post('/api/dedup/batch', data),

  // 获取指纹库统计
  getStats: () => api.get('/api/dedup/stats')
}

// 统计相关 API
export const statsApi = {
  // 获取统计数据
  getStats: () => api.get('/api/stats'),

  // 工作台流水线各环节数量
  getPipelineSummary: () => api.get('/api/pipeline/summary'),

  // 获取趋势数据
  getTrends: (days = 7) => api.get('/api/stats/trends', { params: { days } }),

  // 获取平台分布
  getPlatformDistribution: () => api.get('/api/stats/platforms'),

  // 获取类型分布
  getTypeDistribution: () => api.get('/api/stats/types')
}

// 提示词实验室 API
export const promptLabApi = {
  createJob: (data) => api.post('/api/prompt-lab/jobs', data),
  createJobSync: (data) => api.post('/api/prompt-lab/jobs/sync', data),
  listJobs: (params) => api.get('/api/prompt-lab/jobs', { params }),
  getJob: (id) => api.get(`/api/prompt-lab/jobs/${id}`),
  getEvents: (id, after = 0) => api.get(`/api/prompt-lab/jobs/${id}/events`, { params: { after } }),
  intervene: (id, data) => api.post(`/api/prompt-lab/jobs/${id}/interventions`, data),
  getDefaults: () => api.get('/api/prompt-lab/defaults'),
  openWebSocket: (id) => new WebSocket(wsUrl(`/api/prompt-lab/ws/jobs/${id}`))
}

// 提示词管理 API
export const promptApi = {
  // 获取维度枚举（平台/类型/主题词/风格/片段）
  getMeta: () => api.get('/api/prompts/meta'),

  // 列表：DB 覆盖 + 内置默认合并视图
  getList: (params) => api.get('/api/prompts', { params }),

  // 获取单条模板详情
  getById: (id) => api.get(`/api/prompts/${id}`),

  // 新增模板
  create: (data) => api.post('/api/prompts', data),

  // 更新模板（自动生成新版本）
  update: (id, data) => api.put(`/api/prompts/${id}`, data),

  // 删除模板（默认软删；hard=true 为物理删）
  delete: (id, hard = false) => api.delete(`/api/prompts/${id}`, { params: { hard } }),

  // 版本列表
  getVersions: (id) => api.get(`/api/prompts/${id}/versions`),

  // 回滚到指定版本
  rollback: (id, versionNo) => api.post(`/api/prompts/${id}/rollback/${versionNo}`),

  // 恢复出厂（物理删除"覆盖内置"的条目）
  resetToBuiltin: (id) => api.post(`/api/prompts/reset/${id}`),

  // 实时预览最终 Prompt
  preview: (data) => api.post('/api/prompts/preview', data),

  // 获取所有内置默认（调试用）
  getAllBuiltin: () => api.get('/api/prompts/builtin/all')
}

// 主题词库 API
export const topicApi = {
  getMeta: () => api.get('/api/topics/meta'),
  getList: (params) => api.get('/api/topics', { params }),
  getByTag: (tag) => api.get(`/api/topics/${tag}`),
  create: (data) => api.post('/api/topics', data),
  update: (tag, data) => api.put(`/api/topics/${tag}`, data),
  delete: (tag, hard = false) => api.delete(`/api/topics/${tag}`, { params: { hard } }),
  reset: (tag) => api.post(`/api/topics/reset/${tag}`)
}

// 话题池 API
export const topicPoolApi = {
  getList: (params) => api.get('/api/topic-pool', { params }),
  getStats: (params) => api.get('/api/topic-pool/stats', { params: params || {} }),
  getEntry: (id) => api.get(`/api/topic-pool/${id}`),
  generate: (data) => api.post('/api/topic-pool/generate', data, { timeout: 300000 }),
  getGenerateStatus: (jobId) => api.get(`/api/topic-pool/generate/${jobId}/status`),
  addManual: (data) => api.post('/api/topic-pool/manual', data),
  consume: (data) => api.post('/api/topic-pool/consume', data),
  resetStatus: (id, newStatus = 'unused') => api.post(`/api/topic-pool/${id}/reset`, { new_status: newStatus }),
  delete: (id) => api.delete(`/api/topic-pool/${id}`)
}

// 知乎问答 API（拆方向含多次 WebSearch + LLM，耗时常超过默认 30s）
const ZHIHU_QA_DIRECTIONS_TIMEOUT_MS = 300000

export const zhihuQaApi = {
  getPrompts: () => api.get('/api/zhihu-qa/prompts'),
  getArticlePrompts: () => api.get('/api/zhihu-qa/article-prompts'),
  previewPrompts: (data) => api.post('/api/zhihu-qa/preview-prompts', data),
  generateDirections: (data) =>
    api.post('/api/zhihu-qa/directions', data, { timeout: ZHIHU_QA_DIRECTIONS_TIMEOUT_MS }),
  startGenerate: (data) => api.post('/api/zhihu-qa/generate', data),
  getGenerateStatus: (jobId) => api.get(`/api/zhihu-qa/generate/${jobId}/status`),
}

// Tabstory · Quora 发帖生成器
export const tabstoryApi = {
  getOptions: () => api.get('/api/tabstory/options'),
  // SSE 流式生成走原生 fetch（axios 不便处理 SSE），返回 Response 让调用方自己读 reader
  generateStream: (data) => fetch(`${resolveApiBaseURL()}/api/tabstory/generate-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data || {}),
  }),
  save: (data) => api.post('/api/tabstory/save', data),
  getPrompts: () => api.get('/api/tabstory/prompts'),
  previewPrompts: (data) => api.post('/api/tabstory/preview-prompts', data),
  // 联网搜索：默认前端超时 60s，因为 Gemini google_search 可能跑较久
  websearch: (data) => api.post('/api/tabstory/websearch', data, { timeout: 60000 }),
}

// 专栏口播 API（联网检索 + 长正文，单次较慢）
const COLUMN_ORAL_PREVIEW_TIMEOUT_MS = 180000
const COLUMN_ORAL_DIRECTIONS_TIMEOUT_MS = 120000

export const columnOralApi = {
  getPrompts: () => api.get('/api/column-oral/prompts'),
  getArticlePrompts: () => api.get('/api/column-oral/article-prompts'),
  previewPrompts: (data) => api.post('/api/column-oral/preview-prompts', data),
  // AI 拆方向（基于主题 + 可选 理论/案例/补充说明）
  generateDirections: (data) =>
    api.post('/api/column-oral/directions', data, { timeout: COLUMN_ORAL_DIRECTIONS_TIMEOUT_MS }),
  // 仅做联网检索（让用户先看素材）
  previewMaterials: (data) =>
    api.post('/api/column-oral/preview-materials', data, { timeout: COLUMN_ORAL_PREVIEW_TIMEOUT_MS }),
  startGenerate: (data) => api.post('/api/column-oral/generate', data),
  getGenerateStatus: (jobId) => api.get(`/api/column-oral/generate/${jobId}/status`),
}

// 口播转视频 API（MVP）
export const voice2videoApi = {
  startGenerate: (data) => api.post('/api/voice2video/generate', data),
  getGenerateStatus: (jobId) => api.get(`/api/voice2video/generate/${jobId}/status`),
}

// 内容数据分析 API（按发布周期 + 最新快照）
export const contentAnalyticsApi = {
  importCsv: (file, params = {}) => {
    const form = new FormData()
    form.append('file', file)
    return api.post('/api/content-analytics/import', form, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
  },
  getMeta: () => api.get('/api/content-analytics/meta'),
  getCockpit: (params) => api.get('/api/content-analytics/cockpit', { params }),
  getLeaderboard: (params) => api.get('/api/content-analytics/leaderboard', { params }),
  getFollowerTimeseries: (params) =>
    api.get('/api/content-analytics/followers/timeseries', { params }),
  getMatrixBoard: (params) =>
    api.get('/api/content-analytics/matrix-board', { params }),
  getGroups: () => api.get('/api/content-analytics/groups'),
  exportReportUrl: (days = 7) =>
    `/api/content-analytics/report.xlsx?days=${days}`,
  getOverview: (params) => api.get('/api/content-analytics/overview', { params }),
  getTrend: (params) => api.get('/api/content-analytics/trend', { params }),
  getWorks: (params) => api.get('/api/content-analytics/works', { params }),
  getTopWorks: (params) => api.get('/api/content-analytics/works/top', { params }),
  getWorkHistory: (workDbId) => api.get(`/api/content-analytics/works/${workDbId}/history`),
  getBatches: () => api.get('/api/content-analytics/batches'),
  syncPublished: (items, note = '在线拉取') =>
    api.post('/api/content-analytics/sync-published', { items, note }),
  purgePlatform: (platform) =>
    api.post('/api/content-analytics/purge-platform', { platform }),
}

// 手机号资费与账号监控
export const phoneMonitorApi = {
  getPhones: () => api.get('/api/phone-monitor/phones'),
  getStats: () => api.get('/api/phone-monitor/stats'),
  getConfig: () => api.get('/api/phone-monitor/config'),
  updateConfig: (data) => api.put('/api/phone-monitor/config', data),
  check: (notify = false) => api.get('/api/phone-monitor/check', { params: { notify } }),
  getAlertHistory: (limit = 50) =>
    api.get('/api/phone-monitor/alerts/history', { params: { limit } }),
  testMessage: () => api.post('/api/phone-monitor/test-message'),
  addPhone: (data) => api.post('/api/phone-monitor/phones', data),
  updatePhone: (phone, data) => api.put(`/api/phone-monitor/phones/${phone}`, data),
  deletePhone: (phone) => api.delete(`/api/phone-monitor/phones/${phone}`),
  getGroups: () => api.get('/api/phone-monitor/groups'),
  createGroup: (data) => api.post('/api/phone-monitor/groups', data),
  updateGroup: (id, data) => api.put(`/api/phone-monitor/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/api/phone-monitor/groups/${id}`),
}

// B站洗稿 API
// 拉 UP 主投稿（带匿名指纹 + wbi 签名 + 风控重试，单次最长可能 ~10 秒）
const BILI_SPACE_VIDEOS_TIMEOUT_MS = 60000

export const bilibiliRewriteApi = {
  parse: (data) => api.post('/api/bilibili-rewrite/parse', data),
  submit: (data) => api.post('/api/bilibili-rewrite/submit', data),
  tasks: (params) => api.get('/api/bilibili-rewrite/tasks', { params }),
  asrStatus: () => api.get('/api/bilibili-rewrite/asr-status'),
  getPrompts: () => api.get('/api/bilibili-rewrite/prompts'),
  previewPrompts: (data) => api.post('/api/bilibili-rewrite/preview-prompts', data),
  spaceVideos: (data) =>
    api.post('/api/bilibili-rewrite/space-videos', data, {
      timeout: BILI_SPACE_VIDEOS_TIMEOUT_MS,
    }),
  getSpaceCookie: () => api.get('/api/bilibili-rewrite/space-cookie'),
  saveSpaceCookie: (data) => api.put('/api/bilibili-rewrite/space-cookie', data),
}

export const douyinRewriteApi = {
  parse: (data) => api.post('/api/douyin-rewrite/parse', data),
  submit: (data) => api.post('/api/douyin-rewrite/submit', data),
  tasks: (params) => api.get('/api/douyin-rewrite/tasks', { params }),
  asrStatus: () => api.get('/api/douyin-rewrite/asr-status'),
  getPrompts: () => api.get('/api/douyin-rewrite/prompts'),
  previewPrompts: (data) => api.post('/api/douyin-rewrite/preview-prompts', data),
}

// 通用洗稿 API（3 版本并行，耗时常超过默认 30s）
const REWRITE_TIMEOUT_MS = 300000

export const rewriteApi = {
  getOptions: () => api.get('/api/rewrite/options'),
  getPrompts: () => api.get('/api/rewrite/prompts'),
  previewPrompts: (data) => api.post('/api/rewrite/preview-prompts', data),
  generate: (data) => api.post('/api/rewrite/generate', data, { timeout: REWRITE_TIMEOUT_MS }),
}

// 系统设置 API
export const settingsApi = {
  getLlmMeta: () => api.get('/api/settings/llm/meta'),
  getLlmSettings: () => api.get('/api/settings/llm'),
  saveLlmSettings: (data) => api.put('/api/settings/llm', data),
}

// 发布网关 v2 API（统一入口，替代各页面散落的 API_BASE 拼接）
const PUBLISH_V2 = '/api/publish/v2'

export const publishV2Api = {
  getConfig: () => api.get(`${PUBLISH_V2}/config`),
  // 账号矩阵
  listAccounts: (params) => api.get(`${PUBLISH_V2}/accounts`, { params }),
  createAccount: (data) => api.post(`${PUBLISH_V2}/accounts`, data),
  updateAccount: (platform, accountId, params) =>
    api.patch(`${PUBLISH_V2}/accounts/${platform}/${accountId}`, null, { params }),
  listAccountGroups: () => api.get(`${PUBLISH_V2}/accounts/groups`),
  createAccountGroup: (data) => api.post(`${PUBLISH_V2}/accounts/groups`, data),
  updateAccountGroup: (groupId, data) =>
    api.put(`${PUBLISH_V2}/accounts/groups/${groupId}`, data),
  deleteAccountGroup: (groupId) =>
    api.delete(`${PUBLISH_V2}/accounts/groups/${groupId}`),
  assignAccountGroups: (data) => api.post(`${PUBLISH_V2}/accounts/groups/assign`, data),
  batchCreateAccounts: (data) => api.post(`${PUBLISH_V2}/accounts/batch`, data),
  refreshProfile: (platform, accountId) =>
    api.post(`${PUBLISH_V2}/accounts/${platform}/${accountId}/refresh-profile`),
  refreshAllProfiles: () => api.post(`${PUBLISH_V2}/accounts/refresh-profiles`),
  getDataSyncStatus: () => api.get(`${PUBLISH_V2}/accounts/data-sync/status`),
  runDataSync: (data = {}) =>
    api.post(`${PUBLISH_V2}/accounts/data-sync/run`, data, { timeout: 30000 }),
  listDataSyncRuns: (params) =>
    api.get(`${PUBLISH_V2}/accounts/data-sync/runs`, { params }),
  retryFailedDataSync: () =>
    api.post(`${PUBLISH_V2}/accounts/data-sync/retry-failed`),
  getCookieHealth: () => api.get(`${PUBLISH_V2}/accounts/cookie-health`),
  checkCookieHealth: (data) =>
    api.post(`${PUBLISH_V2}/accounts/cookie-health/check`, data || { live: true }, { timeout: 30000 }),
  importCookies: (data) => api.post(`${PUBLISH_V2}/accounts/import-cookies`, data),
  // 易媒：点一次，服务器 SSH 拉取本机 Cookie
  yimeiStatus: () => api.get(`${PUBLISH_V2}/yimei/status`),
  yimeiPull: (data = {}) =>
    api.post(`${PUBLISH_V2}/yimei/pull`, data, { timeout: 300000 }),
  // 发布队列
  enqueue: (data) => api.post(`${PUBLISH_V2}/enqueue`, data),
  enqueueBatch: (data) => api.post(`${PUBLISH_V2}/enqueue/batch`, data),
  listJobs: (params) => api.get(`${PUBLISH_V2}/jobs`, { params }),
  cancelJob: (jobId) => api.post(`${PUBLISH_V2}/jobs/${jobId}/cancel`),
  // 平台与统计
  getPlatforms: () => api.get(`${PUBLISH_V2}/platforms`),
  getStats: () => api.get(`${PUBLISH_V2}/stats`),
  startWorker: () => api.post(`${PUBLISH_V2}/worker/start`),
  // 已发作品（多平台统一入口）
  getPublishedWorks: (platform, accountId, params) =>
    api.get(`${PUBLISH_V2}/accounts/${platform}/${accountId}/published-works`, {
      params,
      timeout: 120000,
    }),
  getWorkDetail: (platform, accountId, workId, params) =>
    api.get(`${PUBLISH_V2}/accounts/${platform}/${accountId}/works/${encodeURIComponent(workId)}/detail`, {
      params,
      timeout: 60000,
    }),
  getXhsNoteComments: (accountId, noteId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/accounts/xiaohongshu/${encodeURIComponent(accountId)}/notes/${encodeURIComponent(noteId)}/comments`,
      { params, timeout: 120000 },
    ),
  /** accountId 推荐传小红书 UID，兼容遗留 mp_xxx */
  getXhsDatacenterBoard: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/xiaohongshu/${encodeURIComponent(accountId)}/datacenter/board`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getXhsGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/xiaohongshu/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  syncAllXhsDatacenterBoards: (params = {}) =>
    api.post(
      `${PUBLISH_V2}/xiaohongshu/datacenter/sync-all`,
      null,
      { params, timeout: 600000 },
    ),
  /** accountId 推荐传小红书 UID；未传 end_day 时后端默认截止昨天 */
  getXhsDatacenterHistory: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/xiaohongshu/${encodeURIComponent(accountId)}/datacenter/history`,
      { params, timeout: 30000 },
    ),
  getXhsDatacenterNote: (accountId, noteId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/xiaohongshu/${encodeURIComponent(accountId)}/datacenter/notes/${encodeURIComponent(noteId)}`,
      { params, timeout: params?.sync ? 120000 : 30000 },
    ),
  getZhihuDatacenterBoard: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/zhihu/${encodeURIComponent(accountId)}/datacenter/board`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getZhihuGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/zhihu/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  getMatrixGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  /** 多分组/全账号看板；group_ids 空字符串或不传 = 全展示，重叠账号后端去重 */
  getMatrixScopeDatacenterBoard: (params = {}) =>
    api.get(`${PUBLISH_V2}/groups/datacenter/board`, {
      params,
      timeout: params?.sync ? 600000 : 120000,
    }),
  getMatrixDatacenterOverview: (params = {}) =>
    api.get(`${PUBLISH_V2}/datacenter/matrix/overview`, {
      params,
      timeout: params?.sync ? 600000 : 120000,
    }),
  getDouyinDatacenterBoard: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/douyin/${encodeURIComponent(accountId)}/datacenter/board`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getDouyinDatacenterWork: (accountId, itemId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/douyin/${encodeURIComponent(accountId)}/datacenter/works/${encodeURIComponent(itemId)}`,
      { params, timeout: params?.sync ? 180000 : 60000 },
    ),
  getDouyinGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/douyin/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  getToutiaoDatacenterBoard: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/toutiao/${encodeURIComponent(accountId)}/datacenter/board`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getToutiaoGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/toutiao/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  getBaijiahaoDatacenterBoard: (accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/baijiahao/${encodeURIComponent(accountId)}/datacenter/board`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getBaijiahaoGroupDatacenterBoard: (groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/baijiahao/groups/${encodeURIComponent(groupId)}/datacenter/board`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  getUnifiedDatacenterGroup: (platform, groupId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/datacenter/${encodeURIComponent(platform)}/groups/${encodeURIComponent(groupId)}`,
      { params, timeout: params?.sync ? 600000 : 60000 },
    ),
  getUnifiedDatacenterAccount: (platform, accountId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/datacenter/${encodeURIComponent(platform)}/accounts/${encodeURIComponent(accountId)}`,
      { params, timeout: params?.sync ? 180000 : 30000 },
    ),
  getUnifiedDatacenterWork: (platform, accountId, workId, params = {}) =>
    api.get(
      `${PUBLISH_V2}/datacenter/${encodeURIComponent(platform)}/accounts/${encodeURIComponent(accountId)}/works/${encodeURIComponent(workId)}`,
      { params, timeout: params?.sync ? 180000 : 60000 },
    ),
  coverProxyUrl: (url) => {
    if (!url) return ''
    const raw = String(url).trim()
    if (!/^https?:\/\//i.test(raw)) return raw
    return `${PUBLISH_V2}/media/cover-proxy?url=${encodeURIComponent(raw)}`
  },
  // 兼容旧调用
  getPublishedNotes: (accountId, params) =>
    api.get(`${PUBLISH_V2}/xiaohongshu/${accountId}/published-notes`, { params }),
  discoverySearch: (data) => api.post(`${PUBLISH_V2}/xiaohongshu/discovery/search`, data),
  discoveryNoteDetail: (data) => api.post(`${PUBLISH_V2}/xiaohongshu/discovery/note-detail`, data),
  discoverySave: (data) => api.post(`${PUBLISH_V2}/xiaohongshu/discovery/save`, data),
  discoveryLibrary: (params) => api.get(`${PUBLISH_V2}/xiaohongshu/discovery/library`, { params }),
  // 抖音发现
  douyinDiscoverySearch: (data) => api.post(`${PUBLISH_V2}/douyin/discovery/search`, data),
  douyinDiscoveryDetail: (data) => api.post(`${PUBLISH_V2}/douyin/discovery/video-detail`, data),
  douyinDiscoverySave: (data) => api.post(`${PUBLISH_V2}/douyin/discovery/save`, data),
  douyinDiscoveryLibrary: (params) => api.get(`${PUBLISH_V2}/douyin/discovery/library`, { params }),
}

/** Content Ops Agent · 策略 / 定时调研 / 结构化点选 */
export const contentOpsApi = {
  meta: () => api.get('/api/content-ops/meta'),
  listStrategies: (params) => api.get('/api/content-ops/strategies', { params }),
  getStrategy: (id) => api.get(`/api/content-ops/strategies/${id}`),
  designStrategy: (data) => api.post('/api/content-ops/strategies/design', data, { timeout: 180000 }),
  saveStrategy: (data) => api.post('/api/content-ops/strategies', data),
  listTasks: (params) => api.get('/api/content-ops/tasks', { params }),
  createTask: (data) => api.post('/api/content-ops/tasks', data),
  updateTask: (id, data) => api.patch(`/api/content-ops/tasks/${id}`, data),
  runTask: (id) => api.post(`/api/content-ops/tasks/${id}/run`, {}, { timeout: 300000 }),
  researchRun: (data) => api.post('/api/content-ops/research/run', data, { timeout: 300000 }),
  goldenFlow: (data) => api.post('/api/content-ops/golden-flow', data, { timeout: 300000 }),
  fullCycle: (data) => api.post('/api/content-ops/full-cycle', data, { timeout: 360000 }),
  goldenFlowStreamPath: '/api/content-ops/golden-flow/stream',
  fullCycleStreamPath: '/api/content-ops/full-cycle/stream',
  listRuns: (params) => api.get('/api/content-ops/runs', { params }),
  getRun: (id) => api.get(`/api/content-ops/runs/${id}`),
  selectActions: (data) => api.post('/api/content-ops/actions/select', data, { timeout: 300000 }),
  schedulerStatus: () => api.get('/api/content-ops/scheduler/status'),
  schedulerTick: () => api.post('/api/content-ops/scheduler/tick'),
  getMemory: (params) => api.get('/api/content-ops/memory', { params }),
  updateMemory: (data) => api.put('/api/content-ops/memory', data),
  listCalendar: (params) => api.get('/api/content-ops/calendar', { params }),
  createCalendar: (data) => api.post('/api/content-ops/calendar', data),
  patchCalendar: (id, data) => api.patch(`/api/content-ops/calendar/${id}`, data),
  linkWork: (id, data) => api.post(`/api/content-ops/calendar/${id}/link-work`, data),
  listArtifacts: (params) => api.get('/api/content-ops/artifacts', { params }),
  artifactFileUrl: (id) => `/api/content-ops/artifacts/${id}/file`,
  listProposals: (params) => api.get('/api/content-ops/proposals', { params }),
  scanProposals: () => api.post('/api/content-ops/proposals/scan'),
  decideProposal: (id, data) => api.post(`/api/content-ops/proposals/${id}/decide`, data),
  weeklyPlan: (data) => api.post('/api/content-ops/weekly-plan', data, { timeout: 180000 }),
  listWeeklyPlans: (params) => api.get('/api/content-ops/weekly-plans', { params }),
  review: (data) => api.post('/api/content-ops/review', data, { timeout: 120000 }),
  materializeCalendar: (id) => api.post(`/api/content-ops/calendar/${id}/materialize`),
  enqueueCalendar: (id, data) => api.post(`/api/content-ops/calendar/${id}/enqueue`, data, { timeout: 120000 }),
  enqueueApproved: (data) => api.post('/api/content-ops/calendar/enqueue-approved', data, { timeout: 300000 }),
  listWorkspaces: () => api.get('/api/content-ops/workspaces'),
  getWorkspace: (id) => api.get(`/api/content-ops/workspaces/${id}`),
  createWorkspace: (data) => api.post('/api/content-ops/workspaces', data),
  updateWorkspace: (id, data) => api.patch(`/api/content-ops/workspaces/${id}`, data),
  addWorkspaceMember: (id, data) => api.post(`/api/content-ops/workspaces/${id}/members`, data),
  workspacesForAccount: (params) => api.get('/api/content-ops/workspaces/for-account', { params }),
  overview: (params) => api.get('/api/content-ops/overview', { params }),
  producePackage: (data) => api.post('/api/content-ops/packages/produce', data, { timeout: 360000 }),
  complianceCheck: (data) => api.post('/api/content-ops/compliance/check', data),
  processDueReviews: () => api.post('/api/content-ops/reviews/process-due'),
  attributionRun: (data) => api.post('/api/content-ops/attribution/run', data, { timeout: 120000 }),
  viralDecompose: (data) => api.post('/api/content-ops/viral/decompose', data, { timeout: 180000 }),
  trafficDiagnose: (data) => api.post('/api/content-ops/traffic/diagnose', data),
}

/** 短信管理 API */
export const smsApi = {
  listDevices: () => api.get('/api/sms/devices'),
  createDevice: (data) => api.post('/api/sms/devices', data),
  updateDevice: (id, data) => api.put('/api/sms/devices/' + id, data),
  deleteDevice: (id) => api.delete('/api/sms/devices/' + id),
  pollDevice: (id) => api.post('/api/sms/devices/' + id + '/poll'),
  sendSms: (id, data) => api.post('/api/sms/devices/' + id + '/send', data),
  notifications: (afterId = 0) =>
    api.get('/api/sms/notifications', { params: { after_id: afterId, limit: 20 } }),
  // 短信列表（分组视图）
  listGroups: (params) => api.get('/api/sms/messages', { params }),
  // 获取某手机号的全部短信
  listByPhone: (phone, params) => api.get('/api/sms/messages', { params: { phone, ...params } }),
  listSessions: (phone) => api.get('/api/sms/messages/sessions', { params: { phone } }),
  // 获取单条短信详情
  getMessage: (id) => api.get('/api/sms/messages/' + id),
  // 删除单条
  deleteMessage: (id) => api.delete('/api/sms/messages/' + id),
  // 批量删除
  batchDelete: (ids) => api.post('/api/sms/messages/batch-delete', ids),
  // 删除某手机号全部短信
  deleteByPhone: (phone) => api.delete('/api/sms/messages/phone/' + phone),
  // 重新处理
  reprocess: (phone) => api.post('/api/sms/messages/' + phone + '/reprocess'),
  // 导出
  exportCsv: (phone) => api.get('/api/sms/export', { params: { phone } }),
  // 规则 CRUD
  listRules: (phone) => api.get('/api/sms/rules', { params: { phone } }),
  createRule: (data) => api.post('/api/sms/rules', data),
  updateRule: (id, data) => api.put('/api/sms/rules/' + id, data),
  deleteRule: (id) => api.delete('/api/sms/rules/' + id),
  // 钉钉机器人 CRUD
  listRobots: () => api.get('/api/sms/dingtalk-robots'),
  getRobot: (id) => api.get('/api/sms/dingtalk-robots/' + id),
  createRobot: (data) => api.post('/api/sms/dingtalk-robots', data),
  updateRobot: (id, data) => api.put('/api/sms/dingtalk-robots/' + id, data),
  deleteRobot: (id) => api.delete('/api/sms/dingtalk-robots/' + id),
  testRobot: (id) => api.post('/api/sms/dingtalk-robots/' + id + '/test'),
}

/** 小红书评论实时监控 API */
const XHS_COMMENTS = '/api/publish/v2/xiaohongshu/comments'
export const xhsCommentApi = {
  // 监控对象
  listMonitors: (accountId = '') => api.get(`${XHS_COMMENTS}/monitors`, { params: { account_id: accountId } }),
  addMonitor: (data) => api.post(`${XHS_COMMENTS}/monitors`, data),
  deleteMonitor: (id) => api.delete(`${XHS_COMMENTS}/monitors/${id}`),
  // 抓取
  poll: (monitorId) => api.post(`${XHS_COMMENTS}/poll`, null, { params: monitorId ? { monitor_id: monitorId } : {}, timeout: 120000 }),
  // 评论流
  getFeed: (params) => api.get(`${XHS_COMMENTS}/feed`, { params }),
  markRead: (commentItemId) => api.post(`${XHS_COMMENTS}/feed/${commentItemId}/read`),
  markAllRead: (monitorId) => api.post(`${XHS_COMMENTS}/read-all`, null, { params: monitorId ? { monitor_id: monitorId } : {} }),
  reply: (commentItemId, content) => api.post(`${XHS_COMMENTS}/feed/${commentItemId}/reply`, { content }),
  // 关键词
  getKeywords: () => api.get(`${XHS_COMMENTS}/keywords`),
  putKeywords: (keywords) => api.put(`${XHS_COMMENTS}/keywords`, { keywords }),
  // 实时：作品详情里的即时评论（不依赖监控库）
  liveComments: (params) => api.get(`${XHS_COMMENTS}/live`, { params }),
  liveReply: (data) => api.post(`${XHS_COMMENTS}/live-reply`, data),
}

export default api
